import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPostFile, apiGet, apiPatch } from '../services/api';

export interface BudgetPreviewLine {
  inntektslinjeID: string;
  ansvar: string;
  ansvarNavn: string;
  konto: string;
  kontoNavn: string;
  tjenester: string;
  budsjettBeloep: number;
  antallRader: number;
}

interface BudgetPreviewResponse {
  success: boolean;
  data: {
    filename: string;
    totalRowsInSheet: number;
    filteredRows: number;
    aggregatedRows: number;
    zeroBeloepSkipped: number;
    aar: number;
    totalBudsjett: number;
    preview: BudgetPreviewLine[];
  };
}

interface BudgetImportResponse {
  success: boolean;
  data: {
    totalRowsInSheet: number;
    filteredRows: number;
    importedRows: number;
    createdLines: number;
    updatedLines: number;
    totalBudsjett: number;
    aar: number;
  };
}

export interface IncomeLineSummary {
  Ansvar: string;
  AnsvarNavn: string | null;
  AntallLinjer: number;
  TotaltBudsjett: number | null;
  AntallAvtaler: number;
  RisikojustertDekning: number | null;
  Dekningsgrad: number | null;
}

interface SummaryResponse {
  success: boolean;
  data: IncomeLineSummary[];
}

export function useBudgetPreview() {
  return useMutation({
    mutationFn: (file: File) =>
      apiPostFile<BudgetPreviewResponse>('/budget-import', file),
  });
}

export function useBudgetImportConfirm() {
  return useMutation({
    mutationFn: (file: File) =>
      apiPostFile<BudgetImportResponse>('/budget-import/confirm', file),
  });
}

export interface TreeRow {
  SektorID: string | null;
  SektorNavn: string | null;
  RammeomraadeID: string | null;
  RammeomraadeNavn: string | null;
  Ansvar: string;
  AnsvarNavn: string | null;
  Konto: string;
  KontoNavn: string | null;
  Budsjett: number | null;
  AntallAvtaler: number;
  Dekning: number | null;
}

interface TreeResponse {
  success: boolean;
  data: TreeRow[];
}

export function useIncomeLineTree(aar?: string) {
  const params = new URLSearchParams();
  if (aar) params.set('aar', aar);
  const qs = params.toString();

  return useQuery({
    queryKey: ['income-lines-tree', aar],
    queryFn: () => apiGet<TreeResponse>(`/income-lines/tree${qs ? `?${qs}` : ''}`),
  });
}

export function useIncomeLineSummary(aar?: string, sektorId?: string) {
  const params = new URLSearchParams();
  if (aar) params.set('aar', aar);
  if (sektorId) params.set('sektorId', sektorId);
  const qs = params.toString();

  return useQuery({
    queryKey: ['income-lines-summary', aar, sektorId],
    queryFn: () => apiGet<SummaryResponse>(`/income-lines/summary${qs ? `?${qs}` : ''}`),
  });
}

export interface PatchIncomeLineData {
  Tjeneste?: string | null;
  Prosjekt?: string | null;
  Periodisering?: string | null;
}

export function usePatchIncomeLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PatchIncomeLineData }) =>
      apiPatch(`/income-lines/${encodeURIComponent(id)}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['income-lines'] });
    },
  });
}

interface DimensionOption {
  [key: string]: unknown;
}

interface DimensionListResponse {
  data: DimensionOption[];
}

export function useDimensionOptions(type: 'tjeneste' | 'prosjekt' | 'periodisering') {
  return useQuery({
    queryKey: ['dimensions', type],
    queryFn: () => apiGet<DimensionListResponse>(`/dimensions/${type}`),
    staleTime: 10 * 60 * 1000,
  });
}
